import { useState, useRef, useEffect } from 'react';

import {
  Box,
  Button,
  Input,
  List,
  ListItem,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useOutsideClick,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  IconButton,
} from '@chakra-ui/react';

import { ChevronDownIcon, SearchIcon, CloseIcon } from '@chakra-ui/icons';

export interface SelectOption {
  label: string;
  value: string | number;
  isDisabled?: boolean;
}

interface SelectSearchProps {
  options: SelectOption[];
  value?: string | number;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  name?: string;
  allowClear?: boolean;
  allowManualOther?: boolean;
  onFocus?: () => void;
  maxDisplayOptions?: number;
}

const SelectSearch = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isDisabled = false,
  isLoading = false,
  isInvalid = false,
  name,
  allowClear = false,
  allowManualOther = false,
  onFocus,
  maxDisplayOptions,
}: SelectSearchProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isManualInput, setIsManualInput] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300');
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  // Filter options based on search query
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Limit display if maxDisplayOptions is set, otherwise show all
  const displayOptions = maxDisplayOptions
    ? filteredOptions.slice(0, maxDisplayOptions)
    : filteredOptions;
  const hasMoreOptions = maxDisplayOptions
    ? filteredOptions.length > maxDisplayOptions
    : false;

  // Find selected option label
  const selectedOption = options.find((option) => option.value === value);

  // Check if current value doesn't match any option (for manual input mode)
  const valueNotInOptions = allowManualOther && value && !selectedOption;

  // Effect to set manual input mode if value doesn't match any option
  useEffect(() => {
    if (allowManualOther && value && !selectedOption) {
      setIsManualInput(true);
    } else if (!value) {
      setIsManualInput(false);
    }
  }, [value, selectedOption, allowManualOther]);

  // Close dropdown when clicking outside
  useOutsideClick({
    ref: ref,
    handler: () => setIsOpen(false),
  });

  // Focus on input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle option selection
  const handleSelect = (optionValue: string | number) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
    setIsManualInput(false);
  };

  // Handle "Other" selection
  const handleSelectOther = () => {
    setIsManualInput(true);
    setIsOpen(false);
    setSearchQuery('');
    onChange('');
  };

  // Handle manual input change
  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // Handle clear selection
  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setIsManualInput(false);
  };

  return (
    <Box ref={ref} position="relative" width="100%">
      {/* Manual Input Mode */}
      {isManualInput || valueNotInOptions ? (
        <InputGroup>
          <Input
            variant="main"
            name={name}
            value={value || ''}
            onChange={handleManualInputChange}
            placeholder="Enter custom value..."
            isDisabled={isDisabled || isLoading}
            isInvalid={isInvalid}
          />
          {allowClear && value && (
            <IconButton
              aria-label="Clear selection"
              icon={<CloseIcon />}
              size="sm"
              position="absolute"
              right="8px"
              top="50%"
              transform="translateY(-50%)"
              onClick={handleClear}
              variant="ghost"
              _hover={{ background: 'transparent' }}
              title="Clear selection"
              zIndex={1}
            />
          )}
          {allowManualOther && !valueNotInOptions && (
            <Button
              position="absolute"
              right="5px"
              top="4px"
              onClick={() => setIsManualInput(false)}
              size="sm"
              px={2}
            >
              ↩
            </Button>
          )}
        </InputGroup>
      ) : (
        /* Dropdown Mode */
        <Popover
          isOpen={isOpen && !isDisabled && !isLoading}
          onClose={() => setIsOpen(false)}
          placement="bottom"
          autoFocus={false}
          isLazy
          matchWidth={true}
          strategy="fixed"
        >
          <PopoverTrigger>
            <Button
              ref={triggerRef}
              onClick={() => {
                setIsOpen(!isOpen);
                onFocus?.();
              }}
              rightIcon={isLoading ? undefined : <ChevronDownIcon />}
              width="100%"
              justifyContent="space-between"
              fontWeight="normal"
              variant="outline"
              isDisabled={isDisabled || isLoading}
              name={name}
              position="relative"
              pr={selectedOption && allowClear ? '40px' : undefined}
              isLoading={isLoading}
              loadingText={placeholder}
              borderColor={isInvalid ? 'red.600' : undefined}
              _hover={isInvalid ? { borderColor: 'red.600' } : undefined}
              overflow="hidden"
            >
              <Text
                as="span"
                textOverflow="ellipsis"
                overflow="hidden"
                whiteSpace="nowrap"
                flex="1"
                textAlign="left"
              >
                {selectedOption ? selectedOption.label : placeholder}
              </Text>
              {selectedOption && allowClear && !isLoading && (
                <IconButton
                  aria-label="Clear selection"
                  icon={<CloseIcon />}
                  size="xs"
                  position="absolute"
                  right="10px"
                  onClick={handleClear}
                  variant="ghost"
                  _hover={{ background: 'transparent' }}
                  title="Clear selection"
                />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            width="100%"
            border="1px solid"
            borderColor={borderColor}
            _focus={{
              outline: 'none',
              boxShadow: 'outline',
            }}
            boxShadow="md"
            position="absolute"
            zIndex={1400}
          >
            <Box
              position="sticky"
              top="0"
              zIndex={1}
              bg={bgColor}
              borderBottom="1px solid"
              borderColor={borderColor}
            >
              <InputGroup size="md">
                <InputLeftElement pointerEvents="none" pt={2}>
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  ref={inputRef}
                  placeholder="Search options..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  variant="unstyled"
                  px={3}
                  py={3}
                  pl={10}
                  borderRadius={0}
                  width="100%"
                />
              </InputGroup>
            </Box>

            <PopoverBody p={0} maxH="250px" overflowY="auto">
              <List spacing={0} width="100%">
                {allowClear && (
                  <ListItem
                    onClick={() => {
                      onChange(null);
                      setIsOpen(false);
                      setSearchQuery('');
                    }}
                    px={4}
                    py={2}
                    cursor="pointer"
                    bg={value === null ? hoverBgColor : bgColor}
                    _hover={{ bg: hoverBgColor }}
                    transition="background 0.2s"
                    borderBottom="1px solid"
                    borderColor="gray.100"
                  >
                    <Text color="gray.500">{placeholder}</Text>
                  </ListItem>
                )}

                {filteredOptions.length > 0 ? (
                  displayOptions.map((option) => (
                    <ListItem
                      key={option.value}
                      onClick={
                        option.isDisabled
                          ? undefined
                          : () => handleSelect(option.value)
                      }
                      px={4}
                      py={2}
                      cursor={option.isDisabled ? 'not-allowed' : 'pointer'}
                      bg={value === option.value ? hoverBgColor : bgColor}
                      _hover={option.isDisabled ? {} : { bg: hoverBgColor }}
                      transition="background 0.2s"
                      opacity={option.isDisabled ? 0.5 : 1}
                    >
                      <Text>{option.label}</Text>
                    </ListItem>
                  ))
                ) : (
                  <ListItem py={3} px={4} cursor="default" width="100%">
                    <Text color="gray.500" textAlign="center">
                      No options found
                    </Text>
                  </ListItem>
                )}

                {/* "Other" option at the bottom */}
                {allowManualOther && (
                  <ListItem
                    onClick={handleSelectOther}
                    px={4}
                    py={2}
                    cursor="pointer"
                    bg={bgColor}
                    _hover={{ bg: hoverBgColor }}
                    transition="background 0.2s"
                    borderTop="1px solid"
                    borderColor="gray.100"
                  >
                    <Text fontStyle="italic">Other</Text>
                  </ListItem>
                )}
              </List>

              {hasMoreOptions && maxDisplayOptions && (
                <Box
                  py={2}
                  px={4}
                  borderTop="1px solid"
                  borderColor={borderColor}
                >
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    {filteredOptions.length - maxDisplayOptions} more items
                    available. Continue typing to narrow results.
                  </Text>
                </Box>
              )}
            </PopoverBody>
          </PopoverContent>
        </Popover>
      )}
    </Box>
  );
};

export default SelectSearch;
